import { SelectionModel } from '@angular/cdk/collections';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { HttpBodyReqPaginationModel } from '../../../../core/models';
import { Parameter } from '../../../../core/models/util/api-adapter-service-models';
import { AppResponse } from '../../../../core/models/util/api-dto/response-models';
import { ApiAdapterServiceService } from '../../../../core/services';
import { downloadBlobFile } from '../../../../core/util';
import { ContactCategoryModel } from '../../../models';
import { TableModel } from '../../campaign-index/models';

@Component({
    selector: 'lib-new-number-bases',
    templateUrl: './new-number-bases.component.html',
    styleUrls: ['./new-number-bases.component.scss'],
})
export class NewNumberBasesComponent implements OnInit, OnDestroy {
    @Output()
    public targetAudience: EventEmitter<{
        targetAudience: string[];
        numberCount: number;
        isUpload: boolean;
        file: any;
    }>;

    public table: TableModel;
    public isUpload: boolean = false;
    public isLoading: boolean = false;
    public isShowUploadCSV: boolean = false;
    public categorySelected: ContactCategoryModel[] = [];
    public selection = new SelectionModel<ContactCategoryModel>(true, []);
    public extFileAccepted: string[] = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        '.csv',
    ];
    public fileSelected: {
        name: string;
        targetURL: string;
    } = null;

    private categoryParam: HttpBodyReqPaginationModel;
    private subscribers: Subscription[] = [];

    @Input() public isReadOnlyMode: boolean;

    @ViewChild(MatSort) matSort: MatSort;

    constructor(
        private apiAdapterService: ApiAdapterServiceService,
        private toastr: ToastrService
    ) {
        this.targetAudience = new EventEmitter();
    }

    ngOnInit(): void {
        this.categoryParam = new HttpBodyReqPaginationModel();

        this.initTable();
        this.getCategoryList();

        this.targetAudience.emit({
            targetAudience: [],
            numberCount: 0,
            isUpload: this.getCampaignStorage?.generalInformation?.form?.isUpload,
            file: {
                name: this.getCampaignStorage?.targetAudienceFile?.name,
                targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
            },
        });
    }

    private initTable(): void {
        this.table = new TableModel();
        this.table.labels = [
            'select',
            'Category',
            'No. Of Customer',
            'Created Date',
            'Updated Date',
        ];
        this.table.columns = ['select', 'name', 'totalMembers', 'createdDate', 'updatedDate'];
    }

    get getCampaignStorage(): any {
        return localStorage.getItem('telin.campaign')
            ? JSON.parse(localStorage.getItem('telin.campaign'))
            : {};
    }

    private initEdit(): void {
        if (Object.entries(this.getCampaignStorage).length === 0) return;

        const genInfoForm = this.getCampaignStorage?.generalInformation?.form;
        this.isShowUploadCSV = genInfoForm?.isUpload || genInfoForm?.isDownloadVariableXLS;

        this.fileSelected = {
            name: this.getCampaignStorage?.targetAudienceFile?.name,
            targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
        };

        this.table.dataSource.data?.forEach((each) => {
            if (this.getCampaignStorage?.targetAudience?.includes(each.id))
                this.onChecked(true, each);
        });
    }

    private getCategoryList(filter: string = ''): void {
        this.isLoading = true;

        if (this.isReadOnlyMode) this.categoryParam.pageSize = 1000;

        const parameterList: Array<Parameter> = [
            this.initParam('pageNo', this.categoryParam.pageNo, 'number'),
            this.initParam('rowPerPage', this.categoryParam.pageSize, 'number'),
            this.initParam('filter', filter, 'string'),
            this.initParam(
                'platform',
                this.getCampaignStorage?.generalInformation?.form?.medium,
                'string'
            ),
        ];

        if (this.getCampaignStorage?.generalInformation?.form?.medium === 'LINE')
            parameterList.push(
                this.initParam(
                    'accountId',
                    this.getCampaignStorage?.generalInformation?.form?.from?.id,
                    'string'
                )
            );

        if (this.isReadOnlyMode) parameterList.push(this.initParam('summary', true, 'string'));

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/admin/category', 'GET')
            .subscribe((resp: AppResponse<ContactCategoryModel[]>) => {
                if (this.isReadOnlyMode) {
                    this.table.dataSource.data = resp.data?.filter((each) =>
                        this.getCampaignStorage?.targetAudience?.includes(each.id)
                    );

                    this.categorySelected = this.table.dataSource.data;
                    this.isLoading = false;
                    this.initEdit();
                    return;
                }

                this.table.dataSource.data = resp.data;
                this.table.setNextPage(resp.haveNext);
                this.isLoading = false;
                this.initEdit();
            });

        this.subscribers.push(subs);
    }

    private initParam(_name: string, _value: any, _type: string): Parameter {
        return {
            name: _name,
            value: _value,
            in: 'query',
            type: _type,
        };
    }

    public updateSearch(searchValue: string): void {
        this.table.resetPage();
        this.categoryParam.pageNo = this.table.page;
        this.getCategoryList(searchValue);
    }

    public updatePage(event: any): void {
        if (this.table.pageSize !== event?.pageSize) this.table.resetPage();

        this.table.page =
            this.table.pageSize !== event?.pageSize ? this.table.page : event?.pageIndex + 1;
        this.table.pageSize = event?.pageSize;
        this.categoryParam.pageNo = this.table.page;
        this.categoryParam.pageSize = this.table.pageSize;

        this.getCategoryList();
    }

    public sort(): void {
        this.table.dataSource.sort = this.matSort;
    }

    public checkboxLabel(row?: ContactCategoryModel): string {
        if (!row) return `${this.isAllSelected() ? 'deselect' : 'select'} all`;

        return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.name + 1}`;
    }

    public isAllSelected() {
        const numSelected = this.selection.selected.length;
        const numRows = this.table.dataSource.data?.length ?? 0;

        if (
            this.table.dataSource.data &&
            this.table.dataSource.data.length > 0 &&
            this.selection.selected &&
            this.selection.selected.length > 0
        ) {
            for (const data of this.table.dataSource.data) {
                if (this.selection.selected.filter((t) => t.id === data.id).length > 0) continue;

                return false;
            }
        }

        return numSelected === numRows;
    }

    public masterToggle(isChecked: boolean) {
        if (!isChecked) {
            this.selection.clear();
            this.categorySelected = [];

            this.targetAudience.emit({
                targetAudience: [],
                numberCount: 0,
                isUpload: this.isShowUploadCSV,
                file: {
                    name: this.getCampaignStorage?.targetAudienceFile?.name,
                    targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
                },
            });

            return;
        }

        this.selection.select(...this.table.dataSource.data);
        this.categorySelected = this.selection.selected;

        this.targetAudience.emit({
            targetAudience: this.categorySelected.map((t) => t.id),
            numberCount: this.categorySelected.reduce((sum, x) => sum + x.totalMembers, 0),
            isUpload: this.isShowUploadCSV,
            file: {
                name: this.getCampaignStorage?.targetAudienceFile?.name,
                targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
            },
        });
    }

    public onChecked(isChecked: boolean, row: ContactCategoryModel) {
        if (!isChecked) {
            this.selection.deselect(...this.selection.selected.filter((x) => x.id === row.id));
            this.categorySelected = this.selection.selected;

            this.targetAudience.emit({
                targetAudience: this.categorySelected.map((t) => t.id),
                numberCount: this.categorySelected.reduce((sum, x) => sum + x.totalMembers, 0),
                isUpload: this.isShowUploadCSV,
                file: {
                    name: this.getCampaignStorage?.targetAudienceFile?.name,
                    targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
                },
            });

            return;
        }

        this.selection.select(row);
        this.categorySelected = this.selection.selected;

        this.targetAudience.emit({
            targetAudience: this.categorySelected.map((t) => t.id),
            numberCount: this.categorySelected.reduce((sum, x) => sum + x.totalMembers, 0),
            isUpload: this.isShowUploadCSV,
            file: {
                name: this.getCampaignStorage?.targetAudienceFile?.name,
                targetURL: this.getCampaignStorage?.targetAudienceFile?.targetURL,
            },
        });
    }

    public isChecked(row: ContactCategoryModel) {
        const found = this.selection.selected.find((x) => x.id === row.id);
        return found ? true : false;
    }

    public onDownloadSampleService(): void {
        const genInfoForm = this.getCampaignStorage?.generalInformation?.form;
        const isNeedSave = genInfoForm?.isNeedSave;
        const parameterList: Array<Parameter> = [
            this.initParam('platform', genInfoForm?.template?.platform || 'WA', 'string'),
            this.initParam(
                'category',
                this.categorySelected?.map((t) => t.id).join() ?? '',
                'string'
            ),
        ];

        if (isNeedSave) {
            // SKENARIO 1: USE EXISTING / TEMPLATE SUDAH DISAVE
            parameterList.push(
                this.initParam('templateName', genInfoForm?.template?.name, 'string'),
                this.initParam('templateLang', genInfoForm?.template?.lang, 'string')
            );
        } else {
            // SKENARIO 2: CREATE NEW / DIRECT SEND (Belum di-save)
            if (genInfoForm?.template?.name) {
                parameterList.push(
                    this.initParam('templateName', genInfoForm?.template?.name, 'string')
                );
            }
            if (genInfoForm?.template?.lang) {
                parameterList.push(
                    this.initParam('templateLang', genInfoForm?.template?.lang, 'string')
                );
            }

            // Variabel khusus Direct Send
            const safeBodyText = encodeURIComponent(genInfoForm?.text || '');
            parameterList.push(this.initParam('bodyText', safeBodyText, 'string'));

            if (genInfoForm?.template?.header?.text) {
                const safeHeaderText = encodeURIComponent(genInfoForm.template.header.text);
                parameterList.push(this.initParam('headerText', safeHeaderText, 'string'));
            }
        }

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/message/bulkupload/template', 'GET', true)
            .subscribe((resp: AppResponse<any>) => {
                downloadBlobFile(resp, 'Sample-UploadParameter.xlsx');
            });

        this.subscribers.push(subs);
    }

    public onFileSelected(event: Event) {
        const file: FileList = (event.target as HTMLInputElement).files;
        const MAXIMUM_SIZE = 30 * 1024 * 1024;

        if (file.length === 0) return;

        if (file[0].size > MAXIMUM_SIZE) {
            this.toastr.error('Upload media failed, max size 30mb');
            return;
        }

        this.getPresignedUrlService(file[0]);
    }

    private getPresignedUrlService(file: File): void {
        this.isUpload = true;

        const parameterList: Array<Parameter> = [
            { name: 'fileName', value: file.name, in: 'query', type: 'string' },
            { name: 'fileType', value: file.type, in: 'query', type: 'string' },
        ];

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, '/campaign/upload/pre-signed-url', 'GET')
            .subscribe(
                (resp: AppResponse<any>) => {
                    if (resp && resp.data) {
                        this.uploadToS3Service(file, resp.data.url, resp.data.fileName);
                    }
                },
                () => (this.isUpload = false)
            );

        this.subscribers.push(subs);
    }

    private uploadToS3Service(file: File, s3Url: string, fileNameS3: string): void {
        this.apiAdapterService.uploadFileToS3(s3Url, file).subscribe(
            () => {
                this.finalizeTargetFileService(fileNameS3);
            },
            (err) => {
                this.toastr.error('Failed uploading to S3 storage');
                this.isUpload = false;
                console.error('S3 Upload Error:', err);
            }
        );
    }

    private finalizeTargetFileService(fileNameS3: string): void {
        const campaignId = this.getCampaignStorage?.generalInformation?.id;
        const parameterList: Array<Parameter> = [
            {
                name: 'body',
                value: { fileName: fileNameS3 },
                in: 'body',
                type: 'object',
                withoutKeyData: true,
            },
        ];

        const subs = this.apiAdapterService
            .executeApiRequest(parameterList, `/campaign/${campaignId}/targetfile`, 'POST')
            .subscribe(
                (resp: AppResponse<any>) => {
                    if (!resp) return;

                    this.toastr.info('Upload XLSX has been uploaded successfully');
                    this.isUpload = false;

                    this.targetAudience.emit({
                        targetAudience: this.categorySelected.map((t) => t.id),
                        numberCount: this.categorySelected.reduce(
                            (sum, x) => sum + x.totalMembers,
                            0
                        ),
                        isUpload: true,
                        file: {
                            name: fileNameS3,
                            targetURL: resp.data?.targetURL || '',
                        },
                    });
                },
                () => (this.isUpload = false)
            );

        this.subscribers.push(subs);
    }

    ngOnDestroy(): void {
        this.subscribers.forEach((each) => each.unsubscribe());
    }
}
